/**
 * Chercher une référence ou une désignation dans la version en vigueur d'un tarif.
 *
 * Deux usages : retrouver un prix sans passer par une gamme, et VÉRIFIER qu'un import s'est bien passé
 * (taper une référence connue et lire son prix). La recherche se fait côté serveur (un tarif compte plus de
 * vingt mille lignes) : on tape, on attend un quart de seconde, on lit les 50 premières réponses, et
 * « Afficher la suite » charge les suivantes.
 *
 * Le prix n'existe dans la réponse que pour qui détient le droit de le voir : sans lui, la colonne
 * n'apparaît pas — ce n'est pas un champ masqué, la donnée n'a jamais quitté le serveur.
 */
import { useEffect, useRef, useState } from 'react';

import { ApiError, api, type LigneTarifRecherchee, type ResultatRechercheTarif } from '../api/client';
import { dateFrTarif, eurosFr, nombreFr } from '../domaine/tarifs';

const PAGE = 50;
const DELAI_FRAPPE_MS = 250;

export function RechercheTarif({ tarifId, versionId }: { tarifId: string; versionId?: string }) {
  const [saisie, setSaisie] = useState('');
  const [terme, setTerme] = useState('');
  const [lignes, setLignes] = useState<readonly LigneTarifRecherchee[]>([]);
  const [total, setTotal] = useState(0);
  const [version, setVersion] = useState<ResultatRechercheTarif['version']>(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  /** Numéro de la recherche en cours : une réponse tardive d'une recherche périmée est ignorée. */
  const numero = useRef(0);

  // Le terme tapé n'est envoyé qu'après un quart de seconde sans frappe.
  useEffect(() => {
    const minuteur = setTimeout(() => setTerme(saisie.trim()), DELAI_FRAPPE_MS);
    return () => clearTimeout(minuteur);
  }, [saisie]);

  const lire = async (decalage: number) => {
    const moi = ++numero.current;
    setChargement(true);
    setErreur(null);
    try {
      const resultat = await api.rechercherTarif(tarifId, {
        ...(terme ? { search: terme } : {}),
        take: PAGE,
        skip: decalage,
        ...(versionId ? { versionId } : {}),
      });
      if (moi !== numero.current) return;
      setLignes((precedentes) => (decalage === 0 ? resultat.items : [...precedentes, ...resultat.items]));
      setTotal(resultat.total);
      setVersion(resultat.version);
    } catch (e) {
      if (moi !== numero.current) return;
      setErreur(e instanceof ApiError ? e.message : 'Recherche impossible.');
    } finally {
      if (moi === numero.current) setChargement(false);
    }
  };

  // Un autre tarif, une autre version ou un autre terme : on repart du début.
  useEffect(() => {
    void lire(0);
    // `lire` change à chaque rendu : seules ces valeurs relancent la recherche.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarifId, versionId, terme]);

  const avecPrix = lignes.some((l) => l.prix !== undefined);

  return (
    <div className="recherche-tarif">
      <input
        type="text"
        aria-label="Chercher dans le tarif"
        placeholder="Chercher une référence ou une désignation…"
        value={saisie}
        onChange={(e) => setSaisie(e.target.value)}
      />
      <p className="aide">
        {version === null
          ? chargement
            ? 'Recherche…'
            : 'Ce tarif n’a aucune version en vigueur : importez un fichier pour pouvoir y chercher.'
          : terme === ''
            ? `${nombreFr(total)} lignes dans la version ${version.versionNumber} (en vigueur depuis le ${dateFrTarif(version.validFrom)}).`
            : total === 0
              ? `Rien ne correspond à « ${terme} ».`
              : `${nombreFr(total)} ligne${total > 1 ? 's' : ''} pour « ${terme} ».`}
      </p>

      {erreur && <div className="message erreur">{erreur}</div>}

      {lignes.length > 0 && (
        <div className="tableau-defilant">
          <table className="tableau-ecarts">
            <thead>
              <tr>
                <th>Référence</th>
                <th>Désignation</th>
                <th>Famille</th>
                {avecPrix && <th>Prix du tarif HT</th>}
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.reference}>
                  <td>
                    <code>{l.reference}</code>
                  </td>
                  <td>{l.description}</td>
                  <td>{l.famille ?? '—'}</td>
                  {avecPrix && <td>{l.prix === undefined ? '—' : eurosFr(l.prix)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lignes.length < total && (
        <button type="button" className="secondaire" disabled={chargement} onClick={() => void lire(lignes.length)}>
          {chargement ? 'Chargement…' : `Afficher la suite (${nombreFr(total - lignes.length)} de plus)`}
        </button>
      )}
    </div>
  );
}
